import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, CircularProgress, Stack, TextField, Typography, Unstable_Grid2 as Grid
} from '@mui/material';
import {
  Business as BusinessIcon, CheckCircle as CheckIcon, LockOutlined as LockIcon,
  CardMembership as MembershipIcon, LocalOffer as OfferIcon, FitnessCenter as SessionIcon,
  EventAvailable as CalendarIcon
} from '@mui/icons-material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import AppBarContext from 'context/AppBarContext';
import authSlice from 'store/modules/auth';
import uiSlice from 'store/modules/ui';
import { createSnack } from 'common/utils/general';
import routes from 'common/routes';
import { JOIN_ORGANIZATION_BY_CODE } from 'api/graphql/organization';

// Organización, beneficios y membresía del usuario actual (para el desglose en cards).
const ME_ORGANIZATION = gql`
  query MeOrganization {
    me {
      id
      profile_complete
      membership_status
      membership_days_left
      available_sessions
      is_active_plus
      plan_id { id name }
      active_plans { id name }
      organization {
        id
        name
        is_default
        company_covered_percent
        benefits
        plan_discounts { plan_id plan_name client_discount_percent is_active }
      }
    }
  }
`;

const STATUS_LABEL = { ACTIVE: 'Activo', IN_ARREARS: 'En mora', INACTIVE: 'Inactivo' };

function StatCard({ icon, label, value }) {
  return (
    <PremiumCard padding={2.5}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ display: 'flex', color: 'var(--otx-gold-text)' }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{label}</Typography>
          <Typography variant="h6" fontWeight={800} noWrap>{value}</Typography>
        </Box>
      </Stack>
    </PremiumCard>
  );
}

function JoinOrganization() {
  const { t } = useTranslation(['organizations']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const pageTitle = t('myOrganization', { defaultValue: 'Mi organización' });

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const { data, loading, refetch } = useQuery(ME_ORGANIZATION, { fetchPolicy: 'cache-and-network' });
  const [joinByCode, { loading: joining }] = useMutation(JOIN_ORGANIZATION_BY_CODE);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const me = data?.me;
  const currentOrg = me?.organization && !me.organization.is_default ? me.organization : null;

  const handleJoin = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setError('');
    try {
      const res = await joinByCode({ variables: { code: trimmed } });
      const org = res?.data?.joinOrganizationByCode;
      if (org) {
        dispatch(authSlice.actions.updateMeSuccess({ organization: { id: org.id, name: org.name } }));
        setCode('');
        refetch();
      }
    } catch (e) {
      // El backend enmascara el mensaje real con un genérico; mostramos uno claro y accionable.
      const msg = t('joinNotFound', { defaultValue: 'Esa organización no existe. Verifica el código.' });
      setError(msg);
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `org-${Date.now()}`, variant: 'error', message: msg })));
    }
  };

  if (loading && !me) {
    return (
      <Box sx={{ maxWidth: 760, mx: 'auto', py: 4 }}>
        <SEOPageTitle title={pageTitle} />
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress sx={{ color: 'var(--otx-gold-text)' }} /></Stack>
      </Box>
    );
  }

  // ---- Ya pertenece a una organización: desglose en cards ----
  if (currentOrg) {
    // Solo planes con beneficio ACTIVO (coincide con lo que realmente se descuenta en checkout).
    const benefits = (currentOrg.plan_discounts || []).filter((b) => b.is_active);
    const benefitLines = (currentOrg.benefits || '').split('\n').map((s) => s.trim()).filter(Boolean);
    const planName = me?.plan_id?.name || me?.active_plans?.[0]?.name || t('noPlan', { defaultValue: 'Sin plan activo' });
    const statusLabel = STATUS_LABEL[me?.membership_status] || '—';
    return (
      <Box sx={{ maxWidth: 760, mx: 'auto', py: { xs: 2, md: 4 } }}>
        <SEOPageTitle title={pageTitle} />

        {/* Header de la organización */}
        <PremiumCard padding={{ xs: 3, md: 4 }} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ width: 52, height: 52, borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'gold.soft', color: 'var(--otx-gold-text)', flexShrink: 0 }}>
              <BusinessIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('yourOrg', { defaultValue: 'Tu organización' })}</Typography>
              <Typography variant="h5" fontWeight={800} noWrap>{currentOrg.name}</Typography>
            </Box>
          </Stack>
        </PremiumCard>

        {/* Membresía actual */}
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid xs={12} sm={4}><StatCard icon={<MembershipIcon />} label={t('yourPlan', { defaultValue: 'Tu plan' })} value={planName} /></Grid>
          <Grid xs={6} sm={4}><StatCard icon={<CalendarIcon />} label={t('daysLeft', { defaultValue: 'Días restantes' })} value={me?.membership_days_left ?? 0} /></Grid>
          <Grid xs={6} sm={4}><StatCard icon={<SessionIcon />} label={t('sessionsAvailable', { defaultValue: 'Sesiones' })} value={me?.available_sessions ?? 0} /></Grid>
        </Grid>

        <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 1 }} alignItems="center">
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('membershipStatus', { defaultValue: 'Estado de membresía' })}:</Typography>
          <Chip size="small" label={statusLabel} sx={{ fontWeight: 700, backgroundColor: 'surface.raised', color: 'text.primary' }} />
        </Stack>

        {/* Beneficios (planes con descuento) */}
        <Typography variant="h6" fontWeight={800} sx={{ mb: 1.5 }}>{t('yourBenefits', { defaultValue: 'Tus beneficios' })}</Typography>

        {/* Beneficios de texto libre (definidos por admin en la organización) */}
        {benefitLines.length > 0 && (
          <PremiumCard padding={2.5} sx={{ mb: 2 }}>
            <Stack spacing={1}>
              {benefitLines.map((line) => (
                <Stack key={line} direction="row" spacing={1} alignItems="flex-start">
                  <CheckIcon sx={{ fontSize: 18, color: 'var(--otx-gold-text)', mt: '2px', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>{line}</Typography>
                </Stack>
              ))}
            </Stack>
          </PremiumCard>
        )}

        {benefits.length > 0 && (
          <Grid container spacing={2}>
            {benefits.map((b) => (
              <Grid xs={12} sm={6} key={b.plan_id}>
                <PremiumCard padding={2.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body1" fontWeight={800} noWrap>{b.plan_name}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {b.client_discount_percent >= 100
                          ? t('coveredFull', { defaultValue: 'La empresa cubre el total' })
                          : t('coveredPartial', { defaultValue: 'La empresa cubre {{pct}}%', pct: b.client_discount_percent })}
                      </Typography>
                    </Box>
                    <Chip icon={<OfferIcon sx={{ fontSize: 16 }} />} label={`-${b.client_discount_percent}%`} sx={{ backgroundColor: 'gold.soft', color: 'var(--otx-gold-text)', fontWeight: 800 }} />
                  </Stack>
                </PremiumCard>
              </Grid>
            ))}
          </Grid>
        )}

        {benefits.length === 0 && benefitLines.length === 0 && (
          <PremiumCard padding={3}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('noBenefits', { defaultValue: 'Tu organización aún no tiene planes con beneficio configurados.' })}
            </Typography>
          </PremiumCard>
        )}

        {benefits.length > 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>
            {t('benefitAutoNote', { defaultValue: 'Tu beneficio se aplica automáticamente al suscribirte a estos planes.' })}
          </Typography>
        )}
      </Box>
    );
  }

  // ---- Perfil incompleto ----
  if (me && me.profile_complete === false) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', py: { xs: 2, md: 4 } }}>
        <SEOPageTitle title={pageTitle} />
        <PremiumCard padding={4}>
          <Stack spacing={2.5} alignItems="center" sx={{ textAlign: 'center', py: 1 }}>
            <LockIcon sx={{ fontSize: 44, color: 'text.secondary' }} />
            <Typography variant="h6" fontWeight={800}>{t('completeProfileTitle', { defaultValue: 'Completa tu perfil primero' })}</Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              {t('completeProfileBody', { defaultValue: 'Para vincular tu organización necesitas completar tu perfil.' })}
            </Typography>
            <Button onClick={() => navigate(routes.clientProfile.path)} sx={{ ...premiumButtonSx, px: 3 }}>
              {t('goToProfile', { defaultValue: 'Ir a mi perfil' })}
            </Button>
          </Stack>
        </PremiumCard>
      </Box>
    );
  }

  // ---- Perfil completo, en la org por defecto: formulario de código ----
  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', py: { xs: 2, md: 4 } }}>
      <SEOPageTitle title={pageTitle} />
      <PremiumCard padding={4}>
        <Stack spacing={3}>
          <Stack spacing={1} alignItems="center" sx={{ textAlign: 'center' }}>
            <BusinessIcon sx={{ fontSize: 44, color: 'var(--otx-gold-text)' }} />
            <Typography variant="h5" fontWeight={800}>{t('joinTitle', { defaultValue: 'Vincula tu organización' })}</Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              {t('joinHelp', { defaultValue: 'Ingresa el código que te compartieron por correo para acceder a los beneficios de tu organización.' })}
            </Typography>
          </Stack>

          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>
              {t('codeLabel', { defaultValue: 'Código de organización' })}
            </Typography>
            <TextField
              fullWidth
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                if (error) setError('');
              }}
              placeholder={t('codePlaceholder', { defaultValue: 'Ej. AB12CD34' })}
              error={Boolean(error)}
              inputProps={{ style: { letterSpacing: 2, fontWeight: 700 } }}
              sx={{
                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'sessionStatus.absent.fg'
                }
              }}
            />
            {error && (
              <Typography variant="caption" sx={{ color: 'sessionStatus.absent.fg', fontWeight: 600, display: 'block', mt: 0.75 }}>
                {error}
              </Typography>
            )}
          </Box>

          <Button onClick={handleJoin} disabled={joining || !code.trim()} startIcon={joining ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />} sx={{ ...premiumButtonSx }}>
            {t('joinCta', { defaultValue: 'Vincular' })}
          </Button>
        </Stack>
      </PremiumCard>
    </Box>
  );
}

StatCard.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default JoinOrganization;
